/**
 * Resolve a directly-loadable, streaming-capable URL for a FileSource.
 *
 * Used by the audio / video renderers, whose native `<audio>` / `<video>`
 * tags must stream via HTTP range requests (progressive playback + seek)
 * rather than load the whole file first. `resolveExternalUrl()` returns the
 * right thing per source: a short-lived `?token=` URL for auth-scoped
 * workspace routes (the tag can't send a bearer header), or a presigned /
 * public URL for attachments and the Library. Both support range requests.
 *
 * Falls back to `getBlobUrl()` only when a source exposes no external URL —
 * that path buffers the whole file, so it's the last resort, and any
 * `blob:` URL it produces is revoked on unmount.
 */
import { useEffect, useState } from "react";

import type { FileSource } from "../types";

export type FileUrlState =
  | { status: "idle" | "loading" }
  | { status: "ready"; url: string }
  | { status: "error"; error: Error };

export function useFileUrl(source: FileSource): FileUrlState {
  const [state, setState] = useState<FileUrlState>({ status: "idle" });

  useEffect(() => {
    const resolve =
      source.resolveExternalUrl ?? source.getBlobUrl ?? null;
    if (!resolve) {
      setState({
        status: "error",
        error: new Error("Source exposes no loadable URL"),
      });
      return;
    }

    let cancelled = false;
    let producedUrl: string | null = null;
    setState({ status: "loading" });

    Promise.resolve(resolve.call(source))
      .then((url: string) => {
        if (cancelled) {
          if (url.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }
        producedUrl = url;
        setState({ status: "ready", url });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
      if (producedUrl && producedUrl.startsWith("blob:")) {
        URL.revokeObjectURL(producedUrl);
      }
    };
    // Keyed on source identity — same reasoning as useFileText/useFileBlobUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.id]);

  return state;
}
